/*
 * Accessibility shim for the SureBright extended-warranty widget.
 *
 * Vargas v. Listen Up (1:26-cv-13881-WGY) para. 34(c) pleads that the warranty
 * dialog does not take screen-reader focus and cannot be used without sighted
 * help. Inspecting the live widget on 2026-08-31 found the problem is broader
 * than pled:
 *
 *   - The offering cards are plain <div>s with no role and no tabindex. They
 *     are not focusable at all, so no keyboard user can reach them.
 *   - Which option is chosen is carried ONLY by a `selected-offer` CSS class.
 *     There is no radio, no checkbox, no aria-checked or aria-pressed, so the
 *     selection is invisible to assistive technology.
 *   - One option is pre-selected by default, so a screen-reader user is
 *     charged for a warranty they were never told about and cannot decline.
 *
 * We do not control this markup and the app exposes no settings to us: the
 * theme contains no SureBright code and its app-embed `settings` object is
 * empty. The vendor's own SDK has `isDefaultOfferingSelectionEnabled` and
 * `isNoExtendedWarrantyEnabled` flags, but they are account-level and served
 * from SureBright's API. Ask them to change those; this file is what we can do
 * in the meantime.
 *
 * WHY TOGGLE BUTTONS RATHER THAN RADIOS
 * Clicking the already-selected card clears the selection, and the product
 * then adds to the cart with no warranty (verified against production). Radios
 * cannot be unchecked by re-activating them, so radio semantics would describe
 * behaviour the widget does not have - and would hide the only route a
 * keyboard user has to decline. role="button" with aria-pressed matches what
 * the widget actually does, and makes declining reachable.
 *
 * This shim only adds semantics and keyboard access. It never changes what the
 * widget looks like, and it always selects through the vendor's own click
 * handler rather than reimplementing their logic.
 *
 * It is defensive by design: vendor markup can change without notice, so every
 * step is optional and failure is silent. If SureBright ships their own fix,
 * delete this file.
 */
(function () {
  'use strict';

  var GROUP_SELECTOR = '.offering';
  var CARD_SELECTOR = '.offeringCard';
  var SELECTED_CLASS = 'selected-offer';
  var DONE = 'data-a11y-sb';

  var HINT_ID = 'a11y-sb-hint';
  var HINT_TEXT =
    'Optional extended warranty. Choose an option to add it to your order. ' +
    'Activating the option that is already selected removes it, so you can ' +
    'continue without a warranty.';

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  /** One shared, visually hidden description for every warranty group. */
  function hintElement() {
    var hint = document.getElementById(HINT_ID);
    if (hint) return hint;

    hint = document.createElement('div');
    hint.id = HINT_ID;
    hint.className = 'visually-hidden';
    hint.textContent = HINT_TEXT;
    document.body.appendChild(hint);
    return hint;
  }

  function syncPressed(card) {
    card.setAttribute('aria-pressed', card.classList.contains(SELECTED_CLASS) ? 'true' : 'false');
  }

  function enhanceCard(card) {
    if (card.getAttribute(DONE) === 'card') return;
    card.setAttribute(DONE, 'card');

    card.setAttribute('role', 'button');
    if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '0');
    syncPressed(card);

    // Keep the announced state honest when the vendor re-renders or the user
    // selects with a mouse.
    new MutationObserver(function () {
      syncPressed(card);
    }).observe(card, { attributes: true, attributeFilter: ['class'] });

    card.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      // Space would otherwise scroll the page.
      e.preventDefault();
      // Go through the vendor's own handler so selection behaves identically
      // to a mouse click, including the toggle-off that lets you decline.
      card.click();
    });
  }

  function enhanceGroup(group) {
    var cards = Array.prototype.filter.call(group.querySelectorAll(CARD_SELECTOR), isVisible);
    if (!cards.length) return;

    if (group.getAttribute(DONE) !== 'group') {
      group.setAttribute(DONE, 'group');
      group.setAttribute('role', 'group');
      if (!group.getAttribute('aria-label')) {
        group.setAttribute('aria-label', 'Extended product warranty');
      }
      group.setAttribute('aria-describedby', hintElement().id);
    }

    cards.forEach(enhanceCard);
  }

  /**
   * "What's covered?" is a <span> with no role and no tabindex, so Tab skips
   * straight past it. It is the control that opens the dialog described at
   * complaint para. 34(c), which makes it the single most important thing on
   * this widget to be able to reach.
   */
  var TRIGGER_SELECTOR = '.offering-learnMore, [class*="learnMore"], [class*="learn-more"]';

  /*
   * The widget renders differently by viewport. Desktop shows inline option
   * cards with a "What's covered?" link; narrow viewports show an "Add
   * Warranty" button instead - which is the control the complaint actually
   * names at para. 34(c).
   *
   * Matching on class alone would miss whichever variant we have not seen, so
   * also match on the control's own text. Text matching is scoped to small
   * leaf-ish elements inside the SureBright widget so it cannot pick up a
   * paragraph that merely mentions a warranty.
   */
  var TRIGGER_TEXT = /^\s*(add warranty|what.?s covered\??|learn more|see details)\s*$/i;

  function textTriggers() {
    var out = [];
    var scopes = document.querySelectorAll(
      '.offering, [class*="sb-"], [class*="surebright"], [class*="offering"]'
    );

    Array.prototype.forEach.call(scopes, function (scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('*'), function (el) {
        if (el.children.length > 1) return; // not a leaf-ish control
        var own = Array.prototype.filter
          .call(el.childNodes, function (n) { return n.nodeType === 3; })
          .map(function (n) { return n.textContent; })
          .join('');
        if (TRIGGER_TEXT.test(own) && out.indexOf(el) === -1) out.push(el);
      });
    });

    return out;
  }

  function enhanceTriggers() {
    var found = Array.prototype.slice.call(document.querySelectorAll(TRIGGER_SELECTOR));
    textTriggers().forEach(function (el) {
      if (found.indexOf(el) === -1) found.push(el);
    });

    found.forEach(function (el) {
      if (el.getAttribute(DONE) === 'trigger' || !isVisible(el)) return;
      // A real <button> or <a href> is already reachable; leave it alone.
      if (el.tagName === 'BUTTON' || (el.tagName === 'A' && el.hasAttribute('href'))) {
        el.setAttribute(DONE, 'trigger');
        return;
      }
      el.setAttribute(DONE, 'trigger');

      el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      // It opens a dialog; say so rather than leaving the relationship implicit.
      if (!el.hasAttribute('aria-haspopup')) el.setAttribute('aria-haspopup', 'dialog');

      el.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        el.click();
      });
    });
  }

  /** The "powered by SureBright" link is an image link with no alt text. */
  function nameVendorLinks() {
    Array.prototype.forEach.call(document.querySelectorAll('a.surebright-anchor'), function (a) {
      if (a.getAttribute(DONE) === 'anchor') return;
      a.setAttribute(DONE, 'anchor');

      if (!a.getAttribute('aria-label') && !(a.textContent || '').trim()) {
        a.setAttribute('aria-label', 'SureBright, opens in a new tab');
      }
      // Its image carries no alt; it is decorative next to a named link.
      Array.prototype.forEach.call(a.querySelectorAll('img:not([alt])'), function (img) {
        img.setAttribute('alt', '');
      });
    });
  }

  /**
   * Hand any SureBright dialog to the shared focus manager, which moves focus
   * in, traps it, closes on Escape and restores focus on close. This is the
   * behaviour para. 34(c) says is missing.
   */
  function manageDialogs() {
    if (!window.ListenUpA11yDialog || !window.ListenUpA11yDialog.manage) return;

    // Dialogs we can identify positively, from SureBright's own SDK. #lm-modal
    // is what the "What's covered?" / "Add Warranty" trigger opens - the
    // control named at complaint para. 34(c) - and #lm-close-button closes it.
    // The SDK renames both with a "featured-" prefix in some layouts.
    //
    // These are matched by id. An earlier version only ever matched classes,
    // which is why the pled dialog was never adopted even after its trigger
    // was made reachable.
    var KNOWN = [
      { sel: '#lm-modal, #featured-lm-modal', close: '#lm-close-button, #featured-lm-close-button', label: 'What is covered' },
      { sel: '.sb-pp-modal-overlay', close: '[class*="close"]', label: 'Extended warranty' },
      { sel: '.productRegistrationPopup', close: '[class*="close"]', label: 'Product registration' },
    ];

    KNOWN.forEach(function (d) {
      Array.prototype.forEach.call(document.querySelectorAll(d.sel), function (el) {
        if (el.getAttribute(DONE) === 'dialog') return;
        el.setAttribute(DONE, 'dialog');
        try {
          window.ListenUpA11yDialog.manage(el, { label: d.label, closeSelector: d.close });
        } catch (err) {
          /* never let a vendor change break the page */
        }
      });
    });

    // Anything else SureBright-ish is ambiguous, so judge it by shape.
    //
    // A first attempt matched anything with "popup" in its class. That caught
    // `sb-popup-v4-with-product-image`, present and visible on every product
    // page, so the dialog manager treated it as open, hid 46 siblings from
    // assistive tech and trapped Tab on page load.
    //
    // Skipping anything visible when first seen is also wrong: these dialogs
    // are built on demand and appear already visible. So require the shape of
    // a modal - out of flow, covering a large part of the viewport, above the
    // page. Page furniture does not do all three.
    var candidates = document.querySelectorAll(
      '[class*="pp-multi-popup"], [class*="sb-"][class*="popup"], [class*="sb-"][class*="modal"]'
    );

    Array.prototype.forEach.call(candidates, function (el) {
      if (el.getAttribute(DONE) === 'dialog' || el.getAttribute(DONE) === 'not-a-dialog') return;
      if (!isVisible(el)) return; // nothing to judge yet; look again when it opens

      var s = getComputedStyle(el);
      var rect = el.getBoundingClientRect();
      var outOfFlow = s.position === 'fixed' || s.position === 'absolute';
      var large = rect.height > window.innerHeight * 0.4 && rect.width > window.innerWidth * 0.4;
      var above = (parseInt(s.zIndex, 10) || 0) > 0;

      if (!(outOfFlow && large && above)) {
        el.setAttribute(DONE, 'not-a-dialog');
        return;
      }

      el.setAttribute(DONE, 'dialog');
      try {
        window.ListenUpA11yDialog.manage(el, { label: 'Extended warranty details' });
      } catch (err) {
        /* never let a vendor change break the page */
      }
    });
  }

  function apply() {
    try {
      Array.prototype.filter.call(document.querySelectorAll(GROUP_SELECTOR), isVisible).forEach(enhanceGroup);
      enhanceTriggers();
      nameVendorLinks();
      manageDialogs();
    } catch (err) {
      /* silent: this is an enhancement, never a dependency */
    }
  }

  function start() {
    apply();

    // The widget renders asynchronously and re-renders on variant changes, so
    // watch for it rather than assuming it is present at load.
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        apply();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
